import * as React from 'react';
import Button from '@mui/material/Button';
import { styled } from '@mui/material/styles';
import Dialog, { DialogProps } from '@mui/material/Dialog';
import DialogTitle, { DialogTitleProps } from '@mui/material/DialogTitle';
import DialogContent, { DialogContentProps } from '@mui/material/DialogContent';
import IconButton, { IconButtonProps } from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import { Box } from '@mui/material';

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiPaper-root': {
    minWidth: '50%',
    borderRadius: 20,
    overflow: 'hidden',
  },
  '& .MuiDialogContent-root': {
    padding: theme.spacing(2),
  },
}));

type SparkDialogProps = {
  open: boolean;
  onClose: () => void;
  title: React.ReactNode;
  headerButtonText?: string;
  onHeaderButtonClick?: () => void;
  children: React.ReactNode;
  sparkDialogHeader?: React.ReactNode;
  titleContainerStyle?: DialogTitleProps['sx'];
  sx?: DialogProps['sx'];
  disableBackdropClick?: boolean;
  dialogContentStyles?: DialogContentProps['sx'];
  fullScreen?: boolean;
  hideTitle?: boolean;
  hideCloseButton?: boolean;
  closeButtonStyles?: IconButtonProps['sx'];
};

export default function SparkDialog({
  open,
  onClose,
  title,
  headerButtonText,
  onHeaderButtonClick,
  children,
  sx = {},
  sparkDialogHeader = <></>,
  titleContainerStyle,
  disableBackdropClick = false,
  dialogContentStyles,
  fullScreen = false,
  hideTitle = false,
  hideCloseButton = false,
  closeButtonStyles,
}: SparkDialogProps) {
  const shouldRenderHeaderButton = !!onHeaderButtonClick || !!headerButtonText;

  const _handleClose = (reason: 'backdropClick' | 'escapeKeyDown') => {
    if (disableBackdropClick && reason === 'backdropClick') {
      return;
    }
    onClose();
  };

  return (
    <BootstrapDialog
      onClose={(_, reason) => _handleClose(reason)}
      aria-labelledby='customized-dialog-title'
      open={open}
      sx={sx}
      fullScreen={fullScreen}
    >
      {!hideTitle && (
        <DialogTitle
          sx={{
            m: 0,
            p: 2,
            display: 'flex',
            justifyContent: 'flex-start',
            alignItems: 'center',
            bgcolor: (theme) => theme.palette.primary.main,
            ...titleContainerStyle,
          }}
          id='customized-dialog-title'
        >
          <Box fontSize='24px' color={(theme) => theme.palette.common.white}>
            {title}
          </Box>
          {shouldRenderHeaderButton && (
            <Button
              variant='outlined'
              onClick={onHeaderButtonClick}
              sx={{
                mr: 5,
                borderColor: (theme) => theme.palette.common.white,
                color: (theme) => theme.palette.common.white,
              }}
            >
              {headerButtonText}
            </Button>
          )}
        </DialogTitle>
      )}

      {!hideCloseButton && (
        <IconButton
          aria-label='close'
          onClick={onClose}
          sx={{
            position: 'absolute',
            zIndex: 100,
            right: 16,
            top: 16,
            cursor: 'pointer',
            color: (theme) => theme.palette.common.white,
            ...((fullScreen || hideTitle) && {
              color: '#888',
              top: 8,
            }),
            ...(fullScreen &&
              hideTitle && {
                background: 'white',
                opacity: 1,
              }),
            ...closeButtonStyles,
          }}
        >
          <CloseIcon />
        </IconButton>
      )}
      {sparkDialogHeader}
      <DialogContent sx={{ ...dialogContentStyles }}>{children}</DialogContent>
    </BootstrapDialog>
  );
}
